// DTO → view model for the rental-distribution module.
//
// Two invariants this file exists to hold:
//   1. Money stays a STRING. Never Number() a money field here — a float
//      artefact on a screen that credits wallets is a defect (ux-principles §9).
//   2. Lists always emit PaginatedResponse<T>, with totalPages synthesized
//      when the wire omits it, so no container does pagination maths.

import type { PaginatedResponse } from '@shared/types/pagination'
import type {
  FeePartyRole,
  FeeRecurrence,
  FeeUnitBasis,
  FeeValueBasis,
  FeeValueType,
} from '@shared/constants/fees'
import type {
  RentalDeductionLineDto,
  RentalDistributionCountsDto,
  RentalDistributionDetailDto,
  RentalDistributionDto,
  RentalDistributionListMetaDto,
  RentalDistributionRentalPeriodDto,
  RentalDistributionsListDto,
  RentalPayoutCountsDto,
  RentalPayoutDeductionLineDto,
  RentalPayoutDto,
  EligiblePropertyDto,
  PreviewedDeductionDto,
  PreviewedPayoutDto,
  RentalDistributionPreviewDto,
  RentalPayoutsListDto,
  RunApprovalPermissionDto,
  RunPaymentPermissionDto,
  RunReversalPermissionDto,
} from './rentalDistributionsContracts'
import type {
  RentalDeductionLine,
  RentalDistribution,
  RentalDistributionCounts,
  RentalDistributionDetail,
  RentalDistributionRentalPeriod,
  RentalPayout,
  RentalPayoutCounts,
  EligibleProperty,
  PreviewedDeduction,
  PreviewedPayout,
  RentalDistributionPreview,
  RentalPayoutDeductionLine,
  RunApprovalPermission,
  RunPaymentPermission,
  RunReversalPermission,
} from '../types/rentalDistributionTypes'
import type {
  RentalDistributionStatus,
  RentalPayoutStatus,
} from '../constants/rentalDistributionStatus'
import { formatPeriodLabel, periodKeyFromDate } from '../utils/rentalDeductionUtils'

const ZERO = '0.00'
const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 20

/** Money default — keeps every amount a string even when the wire omits it. */
function money(value: string | null | undefined): string {
  return value ?? ZERO
}

function parsePaginationMeta(
  meta: RentalDistributionListMetaDto | undefined,
  rowCount: number,
): PaginatedResponse<never>['pagination'] {
  const page = meta?.page ?? DEFAULT_PAGE
  const limit = meta?.limit ?? (rowCount || DEFAULT_LIMIT)
  const totalItems = meta?.total ?? rowCount
  return {
    page,
    limit,
    totalItems,
    totalPages: meta?.totalPages ?? (limit > 0 ? Math.ceil(totalItems / limit) : 0),
  }
}

export function parseRentalDeductionLine(dto: RentalDeductionLineDto): RentalDeductionLine {
  return {
    id: dto.id,
    feeDefinitionId: dto.feeDefinitionId,
    sequence: dto.sequence,
    feeCode: dto.feeCode,
    feeName: dto.feeName,
    paidByRole: dto.paidByRole as FeePartyRole,
    paidToRole: dto.paidToRole as FeePartyRole,
    recurrence: dto.recurrence as FeeRecurrence,
    valueType: dto.valueType as FeeValueType,
    valueBasis: (dto.valueBasis as FeeValueBasis | null) ?? null,
    unitBasis: (dto.unitBasis as FeeUnitBasis | null) ?? null,
    unitQuantity: dto.unitQuantity ?? null,
    feeValue: dto.feeValue,
    baseAmount: money(dto.baseAmount),
    netAmount: money(dto.netAmount),
    isVatApplicable: dto.isVatApplicable,
    vatPercentage: dto.vatPercentage ?? null,
    vatAmount: money(dto.vatAmount),
    grossAmount: money(dto.grossAmount),
    annualAmount: dto.annualAmount ?? null,
    prorationFactor: dto.prorationFactor ?? null,
    snapshotSource: dto.snapshotSource,
    overrideReason: dto.overrideReason ?? null,
  }
}

export function parseRentalPayoutDeductionLine(
  dto: RentalPayoutDeductionLineDto,
): RentalPayoutDeductionLine {
  return {
    id: dto.id,
    distributionFeeLineId: dto.distributionFeeLineId,
    sequence: dto.sequence,
    feeCode: dto.feeCode,
    feeName: dto.feeName,
    rateLabel: dto.rateLabel ?? null,
    netAmount: money(dto.netAmount),
    vatAmount: money(dto.vatAmount),
    grossAmount: money(dto.grossAmount),
  }
}

export function parseRentalDistribution(dto: RentalDistributionDto): RentalDistribution {
  // The DB stores period_start / period_end, not a period key — derive the
  // key and label here so no consumer reimplements the month maths.
  const periodKey = dto.periodKey ?? periodKeyFromDate(dto.periodStart)
  return {
    id: dto.id,
    offeringId: dto.offeringId,
    propertyId: dto.propertyId,
    propertyName: dto.propertyName,
    propertyRefCode: dto.propertyRefCode ?? null,
    periodStart: dto.periodStart,
    periodEnd: dto.periodEnd,
    periodKey,
    periodLabel: dto.periodLabel ?? formatPeriodLabel(periodKey),
    currencyCode: dto.currencyCode,
    runReference: dto.runReference ?? null,
    // Falls back to the older column: a run declared before Phase 2 has no
    // separate recognised figure, and its gross collected WAS its income.
    recognisedIncomeAmount: money(dto.recognisedIncomeAmount ?? dto.grossCollectedAmount),
    grossCollectedAmount: money(dto.grossCollectedAmount),
    deductibleFees: money(dto.deductibleFees),
    layer2DeductionsAmount: money(dto.layer2DeductionsAmount ?? dto.deductibleFees),
    // Deliberately not defaulted: null means the PMC cut was never attributed
    // to this run, which reads differently from a run where the PMC took nothing.
    layer1DeductionsAmount: dto.layer1DeductionsAmount ?? null,
    shortfallRecoveredAmount: money(dto.shortfallRecoveredAmount),
    shortfallCarriedForwardAmount: money(dto.shortfallCarriedForwardAmount),
    heldAmount: money(dto.heldAmount),
    distributableAmount: money(dto.distributableAmount),
    recordDate: dto.recordDate ?? null,
    status: dto.status as RentalDistributionStatus,
    investorCount: dto.investorCount ?? 0,
    paidCount: dto.paidCount ?? 0,
    failedCount: dto.failedCount ?? 0,
    declaredAt: dto.declaredAt,
    declaredByName: dto.declaredByName ?? null,
    approvedAt: dto.approvedAt ?? null,
    approvedByName: dto.approvedByName ?? null,
    submittedAt: dto.submittedAt ?? null,
    rejectedReason: dto.rejectedReason ?? null,
    reversedAt: dto.reversedAt ?? null,
    reversalReason: dto.reversalReason ?? null,
    externalReferenceId: dto.externalReferenceId ?? null,
    createdAt: dto.createdAt,
  }
}

function parseRentalPeriod(
  dto: RentalDistributionRentalPeriodDto | null | undefined,
): RentalDistributionRentalPeriod | null {
  if (!dto) return null
  return {
    id: dto.id,
    status: dto.status,
    tenantType: dto.tenantType ?? null,
    monthlyRentAmount: dto.monthlyRentAmount ?? null,
    annualRentAmount: dto.annualRentAmount ?? null,
    leaseStart: dto.leaseStart ?? null,
    leaseEnd: dto.leaseEnd ?? null,
  }
}

function parsePayoutCounts(dto: RentalPayoutCountsDto | null | undefined): RentalPayoutCounts {
  return {
    calculated: dto?.calculated ?? 0,
    queued: dto?.queued ?? 0,
    paid: dto?.paid ?? 0,
    failed: dto?.failed ?? 0,
    reversed: dto?.reversed ?? 0,
  }
}

export function parseRentalDistributionDetail(
  dto: RentalDistributionDetailDto,
): RentalDistributionDetail {
  return {
    ...parseRentalDistribution(dto),
    feeLines: (dto.feeLines ?? []).map(parseRentalDeductionLine),
    payoutCounts: parsePayoutCounts(dto.payoutCounts),
    paidAmount: money(dto.paidAmount),
    pendingAmount: money(dto.pendingAmount),
    failedAmount: money(dto.failedAmount),
    totalTokens: dto.totalTokens ?? '0',
    rentalPeriod: parseRentalPeriod(dto.rentalPeriod),
    availableActions: dto.availableActions ?? [],
  }
}

export function parseRentalPayout(dto: RentalPayoutDto): RentalPayout {
  return {
    id: dto.id,
    distributionId: dto.distributionId,
    investorId: dto.investorId,
    investorName: dto.investorName,
    investorEmail: dto.investorEmail ?? '',
    portfolioId: dto.portfolioId,
    fiatWalletId: dto.fiatWalletId,
    currencyCode: dto.currencyCode,
    tokensHeld: dto.tokensHeld,
    sharePercentage: dto.sharePercentage,
    grossAmount: money(dto.grossAmount),
    fees: money(dto.fees),
    netAmount: money(dto.netAmount),
    isHeld: dto.isHeld ?? false,
    heldReason: dto.heldReason ?? null,
    releasedAt: dto.releasedAt ?? null,
    status: dto.status as RentalPayoutStatus,
    feeLines: (dto.feeLines ?? []).map(parseRentalPayoutDeductionLine),
    settledLedgerTxnId: dto.settledLedgerTxnId ?? null,
    paidAt: dto.paidAt ?? null,
    failureReason: dto.failureReason ?? null,
  }
}

export function parseRentalDistributionCounts(
  dto: RentalDistributionCountsDto,
): RentalDistributionCounts {
  return {
    draft: dto.draft ?? 0,
    pendingApproval: dto.pendingApproval ?? 0,
    declared: dto.declared ?? 0,
    approved: dto.approved ?? 0,
    payingOut: dto.payingOut ?? 0,
    completed: dto.completed ?? 0,
    canceled: dto.canceled ?? 0,
    failed: dto.failed ?? 0,
    reversed: dto.reversed ?? 0,
    total: dto.total ?? 0,
    declaredAmount: money(dto.declaredAmount),
    failedAmount: money(dto.failedAmount),
  }
}

export function parseRentalDistributionsList(
  dto: RentalDistributionsListDto,
): PaginatedResponse<RentalDistribution> {
  const rows = dto.data ?? []
  return {
    data: rows.map(parseRentalDistribution),
    pagination: parsePaginationMeta(dto.meta, rows.length),
  }
}

export function parseRentalPayoutsList(
  dto: RentalPayoutsListDto,
): PaginatedResponse<RentalPayout> {
  const rows = dto.data ?? []
  return {
    data: rows.map(parseRentalPayout),
    pagination: parsePaginationMeta(dto.meta, rows.length),
  }
}

/** Distinct 'YYYY-MM' period keys — identity parse, kept for the seam. */
export function parseRentalDistributionPeriods(dto: string[]): string[] {
  return dto ?? []
}

// ─── Declaration ────────────────────────────────────────────────────────────

export function parseEligibleProperty(dto: EligiblePropertyDto): EligibleProperty {
  return {
    propertyId: dto.propertyId,
    propertyName: dto.propertyName,
    propertyRefCode: dto.propertyRefCode ?? null,
    offeringId: dto.offeringId,
    currencyCode: dto.currencyCode,
    suggestedMonthlyRent: dto.suggestedMonthlyRent ?? null,
    leaseStart: dto.leaseStart ?? null,
    leaseEnd: dto.leaseEnd ?? null,
    investorCount: dto.investorCount ?? 0,
    existingDistributionId: dto.existingDistributionId ?? null,
  }
}

export function parseRentalDistributionPreview(
  dto: RentalDistributionPreviewDto,
): RentalDistributionPreview {
  return {
    propertyId: dto.propertyId,
    propertyName: dto.propertyName,
    offeringId: dto.offeringId,
    periodKey: dto.periodKey,
    periodStart: dto.periodStart,
    periodEnd: dto.periodEnd,
    currencyCode: dto.currencyCode,
    recognisedIncomeAmount: money(dto.recognisedIncomeAmount ?? dto.grossCollectedAmount),
    grossCollectedAmount: money(dto.grossCollectedAmount),
    deductibleFees: money(dto.deductibleFees),
    layer2DeductionsAmount: money(dto.layer2DeductionsAmount ?? dto.deductibleFees),
    // Deliberately not defaulted: null means the PMC cut was never attributed
    // to this run, which reads differently from a run where the PMC took nothing.
    layer1DeductionsAmount: dto.layer1DeductionsAmount ?? null,
    shortfallRecoveredAmount: money(dto.shortfallRecoveredAmount),
    shortfallCarriedForwardAmount: money(dto.shortfallCarriedForwardAmount),
    heldAmount: money(dto.heldAmount),
    distributableAmount: money(dto.distributableAmount),
    totalTokens: dto.totalTokens,
    investorCount: dto.investorCount,
    deductions: (dto.deductions ?? []).map(parsePreviewedDeduction),
    payouts: (dto.payouts ?? []).map(parsePreviewedPayout),
  }
}

function parsePreviewedDeduction(dto: PreviewedDeductionDto): PreviewedDeduction {
  return {
    sequence: dto.sequence,
    feeDefinitionId: dto.feeDefinitionId,
    feeCode: dto.feeCode,
    feeName: dto.feeName,
    paidByRole: dto.paidByRole as FeePartyRole,
    paidToRole: dto.paidToRole as FeePartyRole,
    recurrence: dto.recurrence as FeeRecurrence,
    valueType: dto.valueType as FeeValueType,
    valueBasis: (dto.valueBasis as FeeValueBasis | null) ?? null,
    unitBasis: (dto.unitBasis as FeeUnitBasis | null) ?? null,
    unitQuantity: dto.unitQuantity ?? null,
    feeValue: dto.feeValue,
    baseAmount: money(dto.baseAmount),
    netAmount: money(dto.netAmount),
    isVatApplicable: dto.isVatApplicable,
    vatPercentage: dto.vatPercentage ?? null,
    vatAmount: money(dto.vatAmount),
    grossAmount: money(dto.grossAmount),
    annualAmount: dto.annualAmount ?? null,
    prorationFactor: dto.prorationFactor ?? null,
    rateLabel: dto.rateLabel,
  }
}

function parsePreviewedPayout(dto: PreviewedPayoutDto): PreviewedPayout {
  return {
    investorId: dto.investorId,
    investorName: dto.investorName,
    tokensHeld: dto.tokensHeld,
    sharePercentage: dto.sharePercentage,
    grossAmount: money(dto.grossAmount),
    fees: money(dto.fees),
    netAmount: money(dto.netAmount),
    remainderCentsAwarded: dto.remainderCentsAwarded ?? 0,
    feeLines: (dto.feeLines ?? []).map((line) => ({
      sequence: line.sequence,
      feeCode: line.feeCode,
      feeName: line.feeName,
      rateLabel: line.rateLabel,
      netAmount: money(line.netAmount),
      vatAmount: money(line.vatAmount),
      grossAmount: money(line.grossAmount),
    })),
  }
}

/**
 * Defaults to REFUSED when the wire says nothing.
 *
 * Failing closed matters here: a missing field rendering an enabled Approve
 * button would let an operator try a write the server will reject, which is
 * exactly the 403-after-the-click that permission state exists to prevent.
 */
export function parseRunApprovalPermission(
  dto: RunApprovalPermissionDto,
): RunApprovalPermission {
  return {
    canApprove: dto.canApprove ?? false,
    blockedReason: dto.blockedReason ?? null,
  }
}

/** Same fail-closed default as `parseRunApprovalPermission`, for the payment gate. */
export function parseRunPaymentPermission(dto: RunPaymentPermissionDto): RunPaymentPermission {
  return {
    canProcess: dto.canProcess ?? false,
    blockedReason: dto.blockedReason ?? null,
  }
}

/** Same fail-closed default as the other two permission parsers. */
export function parseRunReversalPermission(dto: RunReversalPermissionDto): RunReversalPermission {
  return {
    canReverse: dto.canReverse ?? false,
    blockedReason: dto.blockedReason ?? null,
  }
}
